import { useState } from "react";
import { useSelector } from "react-redux";
import { Alert, Flex, Popconfirm, message } from "antd";
import { getTranslation } from "./translations";
import { Modal, TagList } from "../../commonComponents";
import InputComponent from "../../InputComponent/InputComponent";
import MondayButton from "../../commonComponents/MondayButton/MondayButton";
import {
  DEFAULT_TEMPLATE_ID,
  getTemplateOptions,
  pickTemplateValues,
  useReportTemplates,
} from "./reportTemplates";

const strings = { title: "Titulli", subTitle: "Nëntitulli", note: "Shënim" };

const ReportPersonalizer = ({ open, setOpen, onTemplateApplied }) => {
  const { isDarkMode } = useSelector((state) => state.usersList);
  const {
    templates,
    baseConfig,
    activeTemplateId,
    saveTemplate,
    deleteTemplate,
  } = useReportTemplates();

  // the shared default is read-only, so editing it starts a new template
  const getDraft = (templateId) => {
    const template = templates.find(({ id }) => id === templateId);
    return {
      name: template?.name || "",
      ...pickTemplateValues(template || baseConfig),
    };
  };

  const [selectedId, setSelectedId] = useState(activeTemplateId);
  const [draft, setDraft] = useState(() => getDraft(activeTemplateId));
  const [pending, setPending] = useState(null); // "create" | "update" | "delete"

  const isDefault = selectedId === DEFAULT_TEMPLATE_ID;

  const handleKeyUpdate = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedId(templateId);
    setDraft(getDraft(templateId));
  };

  const handleColumnRemoval = (col) => {
    setDraft((prev) => ({
      ...prev,
      columnDefs: prev.columnDefs.filter((c) => c !== col.value),
    }));
  };

  const getValidationError = (templateId) => {
    const name = draft.name.trim().toLowerCase();
    if (!name) return "Shkruani emrin e shabllonit";
    if (
      templates.some(
        (t) => t.id !== templateId && t.name.trim().toLowerCase() === name,
      )
    ) {
      return "Ekziston tashmë një shabllon me këtë emër";
    }
    if (!draft.columnDefs.length) return "Zgjidhni të paktën një kolonë";
    return null;
  };

  const handleSave = async (asNew) => {
    const templateId = asNew ? null : selectedId;
    const error = getValidationError(templateId);
    if (error) {
      message.warning(error);
      return;
    }

    setPending(asNew ? "create" : "update");
    try {
      await saveTemplate({ ...draft, id: templateId });
      message.success("Shablloni u ruajt me sukses");
      onTemplateApplied?.();
      setOpen(false);
    } catch (e) {
      message.error("Ka ndodhur një gabim gjatë ruajtjes së shabllonit");
      console.error("Error saving report template", e);
    } finally {
      setPending(null);
    }
  };

  const handleDelete = async () => {
    setPending("delete");
    try {
      await deleteTemplate(selectedId);
      // the report falls back to the default when its active template is gone
      if (selectedId === activeTemplateId) onTemplateApplied?.();
      handleTemplateSelect(DEFAULT_TEMPLATE_ID);
      message.success("Shablloni u fshi me sukses");
    } catch (e) {
      message.error("Ka ndodhur një gabim gjatë fshirjes së shabllonit");
      console.error("Error deleting report template", e);
    } finally {
      setPending(null);
    }
  };

  //region RETURN
  return (
    <Modal
      open={open}
      style={{ width: 700 }}
      title={"Personalizo filtrat"}
      onCancel={() => setOpen(false)}
      footer={[
        !isDefault ? (
          <Popconfirm
            key="delete"
            title="A jeni të sigurt që dëshironi të fshini këtë shabllon?"
            okText="Po"
            cancelText="Jo"
            onConfirm={handleDelete}
            disabled={!!pending}
            classNames={{ root: `globalPopover ${isDarkMode ? "dark" : ""}` }}
            getPopupContainer={(trigger) => trigger.parentElement}
          >
            <MondayButton
              className="mondayButtonRed"
              spin={pending === "delete"}
              disabled={!!pending}
            >
              Fshi shabllonin
            </MondayButton>
          </Popconfirm>
        ) : (
          <span key="delete"></span>
        ),
        <Flex key="save" gap={10}>
          <MondayButton
            className={isDefault ? "mondayButtonGreen" : "mondayButtonGrey"}
            spin={pending === "create"}
            disabled={!!pending}
            onClick={() => handleSave(true)}
          >
            Ruaj si shabllon të ri
          </MondayButton>
          {!isDefault ? (
            <MondayButton
              spin={pending === "update"}
              disabled={!!pending}
              onClick={() => handleSave(false)}
            >
              Ruaj ndryshimet
            </MondayButton>
          ) : null}
        </Flex>,
      ]}
    >
      <Flex vertical gap={10} style={{ width: "100%", overflowY: "auto" }}>
        <Flex gap={10}>
          <InputComponent
            type="select"
            label="Shablloni"
            variant={false}
            allowClear={false}
            style={{ flex: 1 }}
            value={selectedId}
            onSelect={handleTemplateSelect}
            customOptions={getTemplateOptions(templates)}
          />
          <InputComponent
            type="input"
            label="Emri i shabllonit"
            maxLength={50}
            style={{ flex: 1 }}
            value={draft.name}
            placeholder={isDefault ? "Emri i shabllonit të ri" : ""}
            onChange={(e) => handleKeyUpdate("name", e.target.value)}
          />
        </Flex>
        {isDefault ? (
          <Alert
            type="info"
            showIcon
            message="Shablloni parazgjedhur është i përbashkët për të gjithë përdoruesit. Ndryshimet ruhen si shabllon i ri vetëm për ju."
          />
        ) : null}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 10,
          }}
        >
          {Object.entries(strings).map(([key, label], i, arr) => (
            <InputComponent
              key={key}
              type="input"
              label={label}
              value={draft[key]}
              style={i === arr.length - 1 ? { gridColumn: "1 / -1" } : {}}
              onChange={(e) => handleKeyUpdate(key, e.target.value)}
            />
          ))}
        </div>
        <InputComponent
          value={null}
          type="select"
          label="Kolonat"
          mode="multiple"
          variant={false}
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) =>
            String(option?.label ?? "")
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          placeholder="Zgjidh një kolonë"
          onSelect={(e) =>
            handleKeyUpdate("columnDefs", draft.columnDefs.concat(e))
          }
          customOptions={(baseConfig.allAppliedColumnDefs || [])
            .filter((el) => !draft.columnDefs.includes(el))
            .map((col) => ({
              value: col,
              label: getTranslation(col),
            }))}
        />
        <TagList
          dndDisabled={false}
          onRemove={handleColumnRemoval}
          style={{ maxHeight: 400, overflowY: "auto" }}
          onReorder={(arr) =>
            handleKeyUpdate(
              "columnDefs",
              arr.map((el) => el.value),
            )
          }
          items={draft.columnDefs.map((el) => ({
            label: getTranslation(el),
            value: el,
          }))}
        />
      </Flex>
    </Modal>
  );
};

export default ReportPersonalizer;
